import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  TrendingUp, TrendingDown, Minus, Compass, AlertTriangle,
  Coins, ShieldAlert, Activity, ArrowUpRight, Crown, Target, ClipboardCheck, Award,
  FileText, X, Loader2, Languages, RefreshCw, Plus, Sparkles, FolderOpen, Radio,
} from "lucide-react";
import CopyButton from "@/components/CopyButton";
import AiText from "@/components/AiText";
import DocxUploader from "@/components/DocxUploader";
import SourceCalcBtn from "@/components/SourceCalcBtn";
import RadarWidget from "@/components/RadarWidget";
import SystemicAnalysisTab from "@/pages/saas/SystemicAnalysisTab";
import { useTranslation } from "react-i18next";
import { useDemoMode } from "@/lib/demoMode";
import { GEO_SCALE_LABELS } from "@/lib/geoScale";
import { PersonaAction } from "@/lib/persona";
import CercleVertueuxWidget from "@/components/CercleVertueuxWidget";
import FreshnessSummaryWidget from "@/components/FreshnessSummaryWidget";
import DataPartialBanner from "@/components/DataPartialBanner";
import InlineGuide from "@/components/InlineGuide";

function formatEur(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} k€`;
  return `${n} €`;
}
function tendIcon(t) {
  if (t === "haussiere") return <TrendingUp className="w-3 h-3 text-[#E24B4A]" />;
  if (t === "baissiere") return <TrendingDown className="w-3 h-3 text-[#1D9E75]" />;
  return <Minus className="w-3 h-3 text-zinc-500" />;
}
function color(score) {
  if (score >= 12) return "var(--risk-high)";
  if (score >= 6) return "var(--risk-medium)";
  return "var(--risk-low)";
}
function gradeBgColor(g) {
  if (!g) return "rgba(255,255,255,0.05)";
  if (g.startsWith("A")) return "rgba(29,158,117,0.12)";
  if (g.startsWith("B")) return "rgba(55,138,221,0.12)";
  if (g.startsWith("C")) return "rgba(239,159,39,0.12)";
  return "rgba(226,75,74,0.12)";
}
function gradeStrokeColor(g) {
  if (!g) return "#A1A1AA";
  if (g.startsWith("A")) return "#1D9E75";
  if (g.startsWith("B")) return "#378ADD";
  if (g.startsWith("C")) return "#EF9F27";
  return "#E24B4A";
}

export default function Executive() {
  const { t, i18n } = useTranslation();
  const isDemo = useDemoMode();
  const execEndpoint = isDemo ? "/executive/summary" : "/workspace/executive/summary";
  const scoreEndpoint = isDemo ? "/score/summary" : "/workspace/score/summary";
  const linkBase = isDemo ? "/demo" : "/app";
  const [data, setData] = useState(null);
  const [score, setScore] = useState(null);
  const [phaseInfo, setPhaseInfo] = useState(null);
  const [orgPhase, setOrgPhase] = useState(null);
  const [error, setError] = useState(null);
  const [showPhase, setShowPhase] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // overview | systemic
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    api.get(execEndpoint).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Impossible de charger la vue executive."));
    api.get(scoreEndpoint).then((r) => setScore(r.data)).catch(() => {});
    api.get("/executive/phase").then((r) => setPhaseInfo(r.data)).catch(() => {});
    if (!isDemo) {
      const loadOrgPhase = () => api.get("/workspace/organisation").then((r) => setOrgPhase(r.data?.phase || "Stabilisation")).catch(() => {});
      loadOrgPhase();
      window.addEventListener("noam:org-updated", loadOrgPhase);
      return () => window.removeEventListener("noam:org-updated", loadOrgPhase);
    }
  }, [execEndpoint, scoreEndpoint, isDemo]);

  const updatePhase = async (p) => {
    try {
      const r = await api.post("/executive/phase", { phase: p });
      setPhaseInfo({ ...phaseInfo, current: r.data.current, next: r.data.next });
      setShowPhase(false);
      setNow(new Date());
    } catch (e) {
      if (typeof console !== "undefined") console.error("Executive: phase update failed", e);
    }
  };

  if (error) return <div className="text-[#E24B4A] text-sm" data-testid="executive-error">{error}</div>;
  if (!data) return <div className="text-zinc-400">{t("common.loading")}</div>;

  if (data.is_empty && !isDemo) {
    return (
      <div className="space-y-5" data-testid="executive-empty">
        <div>
          <div className="flex items-center gap-2 text-[#378ADD] noam-overline">
            <Compass className="w-3 h-3" /> {t("saas.executive.overline")}
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.executive.title")}</h1>
          <p className="text-zinc-400 text-sm mt-1">{t("saas.executive.subtitle")}</p>
        </div>
        <div className="noam-card p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center">
            <Compass className="w-6 h-6 text-[#378ADD]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">{t("saas.axp2.exec_ready")}</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            La consolidation 360° apparaîtra dès que vous aurez créé vos premiers risques.
            Démarrez par cartographier votre univers de risques ou laissez NOAM
            vous proposer un référentiel sectoriel.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            <Link to={`${linkBase}/risques`} className="btn-primary text-sm" data-testid="exec-empty-risques">
              <Plus className="w-4 h-4" /> Créer un risque
            </Link>
            <Link to="/app/configurer" className="btn-ghost text-sm" data-testid="exec-empty-assistant">
              <Sparkles className="w-4 h-4" /> Démarrer avec NOAM
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const niveauTotal = data.risques_par_niveau.strategique + data.risques_par_niveau.tactique + data.risques_par_niveau.operationnel;

  return (
    <div className="space-y-6 fade-up" data-testid="executive-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#378ADD] noam-overline">
            <Compass className="w-3 h-3" /> {t("saas.executive.overline")}
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.executive.title")}</h1>
          <p className="text-zinc-400 text-sm mt-1">
            {t("saas.executive.subtitle")}
          </p>
          <div className="text-[10px] text-zinc-500 mt-1">
            {now.toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isDemo && (
            <button
              onClick={() => setShowPhase(true)}
              className="px-3 py-1.5 rounded-full text-xs border border-[#378ADD]/40 bg-[#378ADD]/10 text-[#B5D4F4] font-medium hover:bg-[#378ADD]/20 transition-colors flex items-center gap-1.5"
              data-testid="exec-phase-btn"
            >
              Phase organisation : <span className="text-white">{phaseInfo?.current || "—"}</span> → {phaseInfo?.next || "—"}
            </button>
          )}
          <button onClick={() => setShowReport(true)} className="btn-primary text-xs" data-testid="exec-report-btn">
            <FileText className="w-3.5 h-3.5" /> {t("saas.executive.report_btn")}
          </button>
        </div>
      </div>

      <InlineGuide
        id="executive-intro"
        title="Ici, vous lisez votre 360° en un coup d'œil"
        bullets={[
          "Vue consolidée des risques par niveau (stratégique / tactique / opérationnel).",
          "Widget « Hygiène des analyses » : repère ce qui est frais vs ce qui mérite une mise à jour.",
          "Bouton « Rapport » pour exporter une synthèse PDF prête à partager au comité.",
        ]}
        hint="Si vous démarrez : ouvrez NOAM pour bâtir un référentiel sectoriel en 5 minutes."
      />

      {/* Tabs bar */}
      <div className="flex items-center gap-1 border-b border-white/5" data-testid="exec-tabs">
        <TabButton active={activeTab === "overview"} onClick={() => setActiveTab("overview")} testid="exec-tab-overview">
          <Compass className="w-3.5 h-3.5" /> {t("saas.executive.tab_overview", { defaultValue: "Vue 360°" })}
        </TabButton>
        <TabButton active={activeTab === "systemic"} onClick={() => setActiveTab("systemic")} testid="exec-tab-systemic">
          <Sparkles className="w-3.5 h-3.5" /> {t("saas.executive.tab_systemic", { defaultValue: "Analyse systémique" })}
        </TabButton>
      </div>

      {/* P0 — Stratégie zone d'opération → zones cibles (visible 360°) */}
      {!isDemo && activeTab === "overview" && <ZoneStrategyCard />}

      {/* P2 — Pouls territorial (signaux immobilier_local des 30 derniers jours par canton) */}
      {!isDemo && activeTab === "overview" && <PulseTerritorialCard />}

      {activeTab === "systemic" ? (
        <SystemicAnalysisTab />
      ) : (
      <>
      {/* Phase-contextual banner (workspace only) */}
      {!isDemo && orgPhase && <PhaseBanner phase={orgPhase} linkBase={linkBase} />}

      {/* Note de pilotage card */}
      {score && (
        <Link to="/app/score" className="noam-card p-5 lg:p-6 flex items-center gap-6 hover:border-white/15 transition-colors group" data-testid="exec-note-card">
          <div className="w-20 h-20 rounded-xl flex flex-col items-center justify-center shrink-0"
            style={{ background: gradeBgColor(score.grade), border: `1px solid ${gradeStrokeColor(score.grade)}` }}>
            <div className="noam-display text-2xl font-bold" style={{ color: gradeStrokeColor(score.grade) }}>{score.grade}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 noam-overline">
              <Award className="w-3 h-3" /> Note de pilotage
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="noam-display text-3xl font-bold">
                {score.overall}<span className="text-base text-zinc-500">/100</span>
              </div>
              <div className={`text-xs flex items-center gap-1 ${score.delta > 0 ? "text-[#1D9E75]" : score.delta < 0 ? "text-[#E24B4A]" : "text-zinc-500"}`}>
                {score.delta > 0 ? <TrendingUp className="w-3 h-3" /> : score.delta < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                {score.delta > 0 ? "+" : ""}{score.delta} pts vs période précédente
              </div>
            </div>
            <div className="text-xs text-zinc-400 mt-2">
              Score consolidé sur 5 piliers — Risques · Conformité · RH · Finance · Opérations
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-[#378ADD] group-hover:text-white transition-colors">
            Voir le détail <ArrowUpRight className="w-4 h-4" />
          </div>
        </Link>
      )}

      {!isDemo && <DataPartialBanner />}

      {/* HEADLINE METRICS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="noam-card p-5 relative overflow-hidden" data-testid="exec-cout-inaction">
          <SourceCalcBtn kpiKey="cout_inaction" />
          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#E24B4A]/10 blur-2xl pointer-events-none" />
          <div className="text-xs text-zinc-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3 text-[#E24B4A]" /> Coût de l'inaction · risques critiques
          </div>
          <div className="noam-display text-3xl font-bold mt-3 text-[#E24B4A]">{formatEur(data.cout_inaction_critique)}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Sur {formatEur(data.cout_inaction_global)} de coût global estimé</div>
        </div>
        <div className="noam-card p-5 relative" data-testid="exec-financabilite">
          <SourceCalcBtn kpiKey="financabilite" />
          <div className="text-xs text-zinc-400 flex items-center gap-1.5"><Coins className="w-3 h-3 text-[#378ADD]" /> {t("saas.axp2.fin_score")}</div>
          <div className="flex items-end justify-between mt-3">
            <div className="noam-display text-3xl font-bold">{data.financabilite.score}<span className="text-base text-zinc-500">/100</span></div>
            <div className={`text-xs flex items-center gap-1 ${data.financabilite.delta >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}`}>
              <TrendingUp className="w-3 h-3" />+{data.financabilite.delta}
            </div>
          </div>
          <div className="h-1.5 mt-3 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-[#378ADD]" style={{ width: `${data.financabilite.score}%` }} /></div>
        </div>
        <div className="noam-card p-5 relative" data-testid="exec-ltv">
          <SourceCalcBtn kpiKey="ltv" />
          <div className="text-xs text-zinc-400 flex items-center gap-1.5"><Activity className="w-3 h-3 text-[#7F77DD]" /> LTV moyen portefeuille</div>
          <div className="flex items-end justify-between mt-3">
            <div className="noam-display text-3xl font-bold">{(data.ltv_moyen.value * 100).toFixed(0)}<span className="text-base text-zinc-500">%</span></div>
            <div className={`text-xs flex items-center gap-1 ${data.ltv_moyen.delta >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}`}>
              {data.ltv_moyen.delta < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
              {data.ltv_moyen.delta > 0 ? "+" : ""}{(data.ltv_moyen.delta * 100).toFixed(0)} pts
            </div>
          </div>
          <div className="text-[11px] text-zinc-500 mt-3">Seuil d'alerte : 75%</div>
        </div>
        <div className="noam-card p-5 relative" data-testid="exec-conformite">
          <SourceCalcBtn kpiKey="conformite" />
          <div className="text-xs text-zinc-400 flex items-center gap-1.5"><ShieldAlert className="w-3 h-3 text-[#1D9E75]" /> {t("saas.axp2.conf_rate")}</div>
          <div className="noam-display text-3xl font-bold mt-3">{data.taux_conformite}<span className="text-base text-zinc-500">%</span></div>
          <div className="h-1.5 mt-3 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-[#1D9E75]" style={{ width: `${data.taux_conformite}%` }} /></div>
        </div>
      </div>

      {/* 3 NIVEAUX */}
      <div className="grid md:grid-cols-3 gap-4">
        <NiveauCard icon={Crown} label={t("saas.axp2.niv_s")} sub={t("saas.axp2.niv_s_sub")} count={data.risques_par_niveau.strategique} total={niveauTotal} color="#378ADD" q={t("saas.axp2.niv_s_q")} testid="niveau-strategique" />
        <NiveauCard icon={Target} label={t("saas.axp2.niv_t")} sub={t("saas.axp2.niv_t_sub")} count={data.risques_par_niveau.tactique} total={niveauTotal} color="#7F77DD" q={t("saas.axp2.niv_t_q")} testid="niveau-tactique" />
        <NiveauCard icon={ClipboardCheck} label={t("saas.axp2.niv_o")} sub={t("saas.axp2.niv_o_sub")} count={data.risques_par_niveau.operationnel} total={niveauTotal} color="#1D9E75" q={t("saas.axp2.niv_o_q")} testid="niveau-operationnel" />
      </div>

      {/* Signaux Tactique — Négociation + Chaînage */}
      {!isDemo && <TacticalSignals linkBase={linkBase} />}
      {!isDemo && <MAPipelineWidget linkBase={linkBase} />}
      {!isDemo && <AnnuaireWidget linkBase={linkBase} />}
      {!isDemo && <DriveWidget linkBase={linkBase} />}
      {!isDemo && <RadarWidget />}

      {/* V23.14 — Mon cercle vertueux (Bras Armé) */}
      {!isDemo && <CercleVertueuxWidget />}
      {!isDemo && <FreshnessSummaryWidget />}

      {/* TOP RISKS + GOULOTS */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="noam-card p-5 lg:col-span-2" data-testid="exec-top-risks">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="noam-overline">Risques majeurs</div>
              <div className="noam-display text-base font-semibold">{t("saas.axp2.top5")}</div>
            </div>
            <Link to="/app/risques" className="text-xs text-[#378ADD] flex items-center gap-1">Tous les risques <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          <div className="space-y-2">
            {data.top_risks.map((r) => (
              <Link key={r.id} to={`/app/risques/${r.id}`} className="flex items-center gap-3 p-3 rounded-lg border border-white/5 hover:border-white/15 hover:bg-white/[0.02] transition-all" data-testid={`exec-risk-${r.id}`}>
                <span className="w-9 h-9 rounded-md flex items-center justify-center text-xs font-bold shrink-0" style={{ background: `${color(r.score_net)}26`, color: color(r.score_net) }}>{r.score_net}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{r.title}</div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                    <span className="font-mono">{r.code}</span>
                    <span className="opacity-30">·</span>
                    <span className="capitalize">{r.niveau}</span>
                    <span className="opacity-30">·</span>
                    <span className="flex items-center gap-1">{tendIcon(r.tendance)} {r.tendance}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("saas.axp2.cout_inaction")}</div>
                  <div className="text-sm font-semibold text-[#E24B4A]">{formatEur(r.cout_inaction)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <div className="noam-card p-5" data-testid="exec-goulots">
          <div className="noam-overline mb-3">{t("saas.axp2.goulots")}</div>
          <div className="noam-display text-base font-semibold mb-4">{t("saas.axp2.blocages")}</div>
          <div className="space-y-3">
            {data.goulots.map((g, i) => (
              <div key={i} className="border-l-2 border-[#EF9F27] pl-3 py-1" data-testid={`exec-goulot-${i}`}>
                <div className="text-sm text-white">{g.label}</div>
                <div className="text-[11px] text-zinc-500 mt-1">{g.impact}</div>
              </div>
            ))}
          </div>
          <Link to="/app/intelligence" className="mt-5 text-xs text-[#378ADD] flex items-center gap-1">Analyse IA des goulots <ArrowUpRight className="w-3 h-3" /></Link>
        </div>
      </div>
      </>
      )}

      {showPhase && phaseInfo && <PhaseModal info={phaseInfo} onClose={() => setShowPhase(false)} onSave={updatePhase} />}
      {showReport && <ReportModal onClose={() => setShowReport(false)} initialLang={(i18n.language || "fr").slice(0, 2)} orgPhase={orgPhase} />}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
        active ? "text-[#378ADD]" : "text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {children}
      {active && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-[#378ADD]" />}
    </button>
  );
}

function PulseTerritorialCard() {
  const [data, setData] = useState(null);
  const [openCanton, setOpenCanton] = useState(null);
  useEffect(() => {
    api.get("/workspace/realestate/pulse", { params: { days: 30 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ total: 0, by_canton: {} }));
  }, []);
  if (!data) return null;
  const cantons = Object.keys(data.by_canton || {}).sort((a, b) => (data.by_canton[b] - data.by_canton[a]));
  const maxCount = Math.max(1, ...Object.values(data.by_canton || {}));
  // Liste de cantons suisses pour rendu même sans signaux
  const SWISS_CANTONS = ["Genève","Vaud","Valais","Fribourg","Neuchâtel","Jura","Bern","Zurich","Bâle","St-Gall","Tessin","Lucerne","Argovie","Soleure"];
  const display = [...new Set([...cantons, ...SWISS_CANTONS])];
  const openSignals = openCanton ? (data.signals || []).filter((s) => (s.geo_region || "Suisse") === openCanton) : [];
  if (data.total === 0) {
    return (
      <div className="noam-card p-4" data-testid="exec-pulse">
        <div className="flex items-center gap-2 mb-2">
          <Radio className="w-3.5 h-3.5 text-[#1D9E75]" />
          <div className="noam-overline">Pouls territorial (30j)</div>
        </div>
        <p className="text-[11px] text-zinc-500 italic">
          Aucun signal immobilier détecté ces 30 derniers jours. La sentinelle territoriale tourne en arrière-plan
          dès qu'un actif est surveillé dans <Link to="/saas/immobilier" className="text-[#EF9F27] hover:underline">Immobilier → Sources publiques</Link>.
        </p>
      </div>
    );
  }
  return (
    <div className="noam-card p-4 space-y-3" data-testid="exec-pulse">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 text-[#1D9E75] animate-pulse" />
          <div className="noam-overline">Pouls territorial · {data.total} signal{data.total > 1 ? "s" : ""} immobilier{data.total > 1 ? "s" : ""} (30j)</div>
        </div>
        <Link to="/saas/radar" className="text-[10px] text-zinc-500 hover:text-[#378ADD]">Voir dans Radar →</Link>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-7 gap-1.5">
        {display.map((c) => {
          const n = data.by_canton[c] || 0;
          const u = data.unread_by_canton?.[c] || 0;
          const intensity = n > 0 ? Math.min(1, n / maxCount) : 0;
          return (
            <button
              key={c}
              type="button"
              onClick={() => n > 0 && setOpenCanton(openCanton === c ? null : c)}
              disabled={n === 0}
              data-testid={`exec-pulse-canton-${c}`}
              className={`rounded-md p-2 text-left text-[10px] transition-all ${
                openCanton === c ? "ring-2 ring-[#EF9F27]" : ""
              } ${n === 0 ? "opacity-40 cursor-default" : "hover:ring-1 hover:ring-[#378ADD] cursor-pointer"}`}
              style={{
                backgroundColor: n === 0 ? "rgba(255,255,255,0.02)" : `rgba(29,158,117,${0.10 + intensity * 0.55})`,
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <div className="text-zinc-300 truncate">{c}</div>
              <div className="text-base font-bold text-zinc-100">{n}</div>
              {u > 0 && <div className="text-[9px] text-[#EF9F27] font-bold">{u} non lu{u > 1 ? "s" : ""}</div>}
            </button>
          );
        })}
      </div>
      {openCanton && openSignals.length > 0 && (
        <div className="border-t border-white/5 pt-3 space-y-1.5" data-testid="exec-pulse-drawer">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
            Signaux récents — {openCanton}
          </div>
          {openSignals.slice(0, 10).map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-2 border border-white/5 rounded p-2">
              <div className="flex-1 min-w-0">
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-zinc-100 hover:text-[#378ADD] truncate">
                  {s.title}
                </a>
                <div className="text-[10px] text-zinc-500 truncate">
                  {s.entity_name && `${s.entity_name} · `}
                  {s.created_at?.slice(0, 10)}
                  {!s.read && <span className="ml-2 text-[#EF9F27]">●</span>}
                </div>
              </div>
              {s.realestate_asset_id && (
                <Link to={`/saas/immobilier`} className="text-[10px] text-[#EF9F27] hover:underline shrink-0">
                  Actif →
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ZoneStrategyCard() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get("/workspace/positionnement")
      .then((r) => setProfile(r.data?.profile || null))
      .catch(() => {});
  }, []);
  if (!profile) return null;
  const zoneOp = profile.zone_operation;
  const zonesCibles = Array.isArray(profile.zones_cibles) ? profile.zones_cibles : [];
  // Si pas de zones définies (anciens profils), on n'affiche rien
  if (!zoneOp && zonesCibles.length === 0) return null;
  const formatZone = (z) => {
    if (!z) return "—";
    const lab = GEO_SCALE_LABELS[z.scale] || z.scale || "—";
    const loc = [z.region, z.pays].filter(Boolean).join(", ");
    return loc ? `${lab} · ${loc}` : lab;
  };
  return (
    <div className="noam-card p-4 space-y-3" data-testid="exec-zone-strategy">
      <div className="flex items-center gap-2">
        <Target className="w-3.5 h-3.5 text-[#1D9E75]" />
        <div className="noam-overline">{t("saas.axp2.strat_zone")}</div>
        <span className="text-[10px] text-zinc-500 italic">— où on est, où on va</span>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="border border-white/10 rounded-md px-3 py-2 bg-white/[0.02] min-w-[200px]" data-testid="exec-zone-op">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Zone actuelle</div>
          <div className="text-sm font-semibold text-zinc-100 mt-0.5">{formatZone(zoneOp)}</div>
        </div>
        <ArrowUpRight className="w-5 h-5 text-[#EF9F27]" />
        <div className="flex items-center gap-2 flex-wrap" data-testid="exec-zone-cibles">
          {zonesCibles.length === 0 ? (
            <div className="border border-white/10 rounded-md px-3 py-2 bg-white/[0.02] text-[11px] text-zinc-500 italic">
              Aucune zone cible définie · <Link to="/saas/positionnement" className="text-[#EF9F27] hover:underline">en ajouter</Link>
            </div>
          ) : (
            zonesCibles.map((z, i) => (
              <div key={i} className="border border-[#EF9F27]/30 rounded-md px-3 py-2 bg-[#EF9F27]/5">
                <div className="text-[9px] uppercase tracking-wider text-[#EF9F27]">Cible #{i + 1}</div>
                <div className="text-sm font-semibold text-zinc-100 mt-0.5">{formatZone(z)}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function TacticalSignals({ linkBase }) {
  const { t } = useTranslation();
  const [nego, setNego] = useState(null);
  const [chain, setChain] = useState(null);
  useEffect(() => {
    api.get("/workspace/negociations/aggregate/summary").then((r) => setNego(r.data)).catch(() => {});
    api.get("/workspace/chains/aggregate/summary").then((r) => setChain(r.data)).catch(() => {});
  }, []);
  if (!nego && !chain) return null;
  // Hide widget entirely if both modules empty (no signal worth showing)
  if ((nego?.count || 0) === 0 && (chain?.count || 0) === 0) return null;

  const tensionColor = !nego?.tension_avg ? "#A1A1AA"
    : nego.tension_avg >= 65 ? "#E24B4A"
    : nego.tension_avg >= 40 ? "#EF9F27" : "#1D9E75";

  return (
    <div className="noam-card p-5" data-testid="exec-tactical-signals">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="noam-overline">{t("exec.tactical_overline", { defaultValue: "Signaux tactiques" })}</div>
          <div className="noam-display text-base font-semibold">{t("exec.tactical_title", { defaultValue: "Négociation & Chaînage" })}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to={`${linkBase}/negociation`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-tactical-nego-count">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.tactical_nego_count", { defaultValue: "Négociations" })}</div>
          <div className="noam-display text-2xl font-bold mt-1 group-hover:text-[#378ADD]">{nego?.count ?? "—"}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{t("exec.tactical_nego_count_sub", { defaultValue: "en cours" })}</div>
        </Link>
        <Link to={`${linkBase}/negociation`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-tactical-nego-tension">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.tactical_nego_tension", { defaultValue: "Tension moyenne" })}</div>
          <div className="noam-display text-2xl font-bold mt-1" style={{ color: tensionColor }}>{nego?.tension_avg ?? "—"}<span className="text-sm text-zinc-500">/100</span></div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{nego?.ai_generated || 0} {t("exec.tactical_nego_ai", { defaultValue: "lectures IA" })}</div>
        </Link>
        <Link to={`${linkBase}/chainage`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-tactical-chain-count">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.tactical_chain_count", { defaultValue: "Chaînes" })}</div>
          <div className="noam-display text-2xl font-bold mt-1 group-hover:text-[#378ADD]">{chain?.count ?? "—"}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{chain?.total_nodes || 0} {t("exec.tactical_chain_nodes", { defaultValue: "nœuds" })}</div>
        </Link>
        <Link to={`${linkBase}/chainage`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-tactical-chain-levers">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.tactical_chain_levers", { defaultValue: "Leviers identifiés" })}</div>
          <div className="noam-display text-2xl font-bold mt-1 group-hover:text-[#378ADD]">{chain?.ai_levers_identified ?? 0}<span className="text-sm text-zinc-500">/{chain?.count || 0}</span></div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{t("exec.tactical_chain_levers_sub", { defaultValue: "par l'IA" })}</div>
        </Link>
      </div>
    </div>
  );
}


function AnnuaireWidget({ linkBase }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/workspace/annuaire/aggregate/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  if ((data.entities_count || 0) === 0) return null;
  const top = data.top_relations || [];
  return (
    <div className="noam-card p-5" data-testid="exec-annuaire">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="noam-overline">{t("exec.annuaire_overline", { defaultValue: "Annuaire" })}</div>
          <div className="noam-display text-base font-semibold">{t("exec.annuaire_title", { defaultValue: "Réseau & relations" })}</div>
        </div>
        <Link to={`${linkBase}/annuaire`} className="text-xs text-[#378ADD] inline-flex items-center gap-1">
          {t("exec.annuaire_open", { defaultValue: "Ouvrir l'annuaire" })} <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to={`${linkBase}/annuaire`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-annuaire-entities">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.annuaire_entities", { defaultValue: "Entités" })}</div>
          <div className="noam-display text-2xl font-bold mt-1 group-hover:text-[#378ADD]">{data.entities_count ?? 0}</div>
        </Link>
        <Link to={`${linkBase}/annuaire`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.annuaire_actives", { defaultValue: "Entités actives" })}</div>
          <div className="noam-display text-2xl font-bold mt-1 group-hover:text-[#378ADD]">{data.active_entities_count ?? 0}</div>
        </Link>
        <Link to={`${linkBase}/annuaire`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-annuaire-contacts">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.annuaire_contacts", { defaultValue: "Contacts" })}</div>
          <div className="noam-display text-2xl font-bold mt-1 group-hover:text-[#378ADD]">{data.contacts_count ?? 0}</div>
        </Link>
        <Link to={`${linkBase}/annuaire`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.annuaire_active_contacts", { defaultValue: "Contacts actifs" })}</div>
          <div className="noam-display text-2xl font-bold mt-1 group-hover:text-[#378ADD]">{data.active_contacts_count ?? 0}</div>
        </Link>
      </div>
      {top.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{t("exec.annuaire_top", { defaultValue: "Top 3 relations actives" })}</div>
          <div className="space-y-1.5">
            {top.map((r) => {
              const colors = { partenaire: "#EF9F27", actif: "#1D9E75" };
              const c = colors[r.status] || "#888";
              return (
                <Link key={r.id} to={`${linkBase}/annuaire`} className="flex items-center gap-3 p-2 rounded hover:bg-white/5 transition-colors" data-testid={`exec-annuaire-top-${r.id}`}>
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase" style={{ background: `${c}22`, color: c, border: `1px solid ${c}55` }}>{r.status}</span>
                  <span className="flex-1 truncate text-sm text-zinc-200">{r.name}</span>
                  <span className="text-[10px] text-zinc-500 hidden sm:inline">{r.type}</span>
                  {r.contacts_count > 0 && <span className="text-[10px] text-zinc-500">{r.contacts_count} contact(s)</span>}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function MAPipelineWidget({ linkBase }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/workspace/courtage/aggregate/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  // Hide widget if no mandates at all (no signal)
  if ((data.active_count || 0) === 0 && (data.total_count || 0) === 0) return null;
  const fmtChf = (n) => {
    if (n === null || n === undefined) return "—";
    return Math.round(Number(n)).toLocaleString("fr-CH").replace(/,/g, "'") + " CHF";
  };
  const top = data.top_qualified || [];
  return (
    <div className="noam-card p-5" data-testid="exec-ma-pipeline">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="noam-overline">{t("exec.ma_overline", { defaultValue: "M&A Pipeline" })}</div>
          <div className="noam-display text-base font-semibold">{t("exec.ma_title", { defaultValue: "Courtage & Cessions" })}</div>
        </div>
        <Link to={`${linkBase}/courtage`} className="text-xs text-[#378ADD] inline-flex items-center gap-1">
          {t("exec.ma_open", { defaultValue: "Ouvrir Courtage" })} <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Link to={`${linkBase}/courtage`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-ma-active">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.ma_active", { defaultValue: "Mandats actifs" })}</div>
          <div className="noam-display text-2xl font-bold mt-1 group-hover:text-[#378ADD]">{data.active_count ?? 0}<span className="text-sm text-zinc-500">/{data.total_count ?? 0}</span></div>
        </Link>
        <Link to={`${linkBase}/courtage`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-ma-volume">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.ma_volume", { defaultValue: "Volume pipeline" })}</div>
          <div className="noam-display text-lg font-bold mt-1 group-hover:text-[#378ADD]">{fmtChf(data.volume_pipeline_chf)}</div>
        </Link>
        <Link to={`${linkBase}/courtage`} className="group block rounded-md border border-white/5 hover:border-[#378ADD]/40 transition-colors p-3 bg-white/[0.02]" data-testid="exec-ma-commission">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.ma_commission", { defaultValue: "Commission attendue" })}</div>
          <div className="noam-display text-lg font-bold mt-1 group-hover:text-[#378ADD]">{fmtChf(data.commission_estimee_chf)}</div>
        </Link>
      </div>
      {top.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{t("exec.ma_top", { defaultValue: "Top 3 dossiers les mieux qualifiés" })}</div>
          <div className="space-y-1.5">
            {top.map((m) => {
              const lc = m.level === "high" ? "#1D9E75" : m.level === "medium" ? "#EF9F27" : "#E24B4A";
              return (
                <Link key={m.id} to={`${linkBase}/courtage`} className="flex items-center gap-3 p-2 rounded hover:bg-white/5 transition-colors" data-testid={`exec-ma-top-${m.id}`}>
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${lc}22`, color: lc, border: `1px solid ${lc}55` }}>{m.score}</span>
                  <span className="flex-1 truncate text-sm text-zinc-200">{m.title}</span>
                  <span className="text-[10px] text-zinc-500 hidden sm:inline">{m.statut}</span>
                  <span className="text-xs text-zinc-400 hidden md:inline">{fmtChf(m.valeur_transaction_chf)}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function NiveauCard({ icon: Icon, label, sub, count, total, color, q, testid }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="noam-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase" style={{ color }}>
          <Icon className="w-3 h-3" /> {label}
        </div>
        <span className="text-[10px] text-zinc-500">{sub}</span>
      </div>
      <div className="mt-4 flex items-end justify-between">
        <div>
          <div className="noam-display text-3xl font-bold">{count}</div>
          <div className="text-[11px] text-zinc-500 mt-1">{pct}% des risques</div>
        </div>
        <div className="w-16 h-1.5 rounded-full bg-white/5 overflow-hidden self-center">
          <div className="h-full" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
      <div className="text-xs text-zinc-400 mt-4 italic border-l-2 pl-3" style={{ borderColor: `${color}66` }}>« {q} »</div>
    </div>
  );
}

function PhaseModal({ info, onClose, onSave }) {
  const [selected, setSelected] = useState(info.current);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()} data-testid="phase-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="noam-overline">Phase organisation</div>
            <div className="noam-display text-lg font-semibold">Choisir la phase actuelle</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2">
          {info.phases.map((p) => (
            <button
              key={p.key}
              onClick={() => setSelected(p.key)}
              className={`w-full text-left p-4 rounded-lg border transition-all ${
                selected === p.key
                  ? "border-[#378ADD] bg-[#378ADD]/10"
                  : "border-white/5 hover:border-white/15 bg-white/[0.02]"
              }`}
              data-testid={`phase-option-${p.key}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-white">{p.key}</div>
                {info.current === p.key && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1D9E75]/15 text-[#1D9E75]">Actuelle</span>
                )}
              </div>
              <div className="text-xs text-zinc-400 mt-1.5">{p.desc}</div>
            </button>
          ))}
        </div>
        <div className="mt-5 p-3 rounded-lg bg-[#378ADD]/8 border border-[#378ADD]/20 flex gap-2 text-xs text-[#B5D4F4]">
          <Compass className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#378ADD]" />
          <span>{info.note_ia}</span>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost flex-1">Annuler</button>
          <button onClick={() => onSave(selected)} disabled={selected === info.current} className="btn-primary flex-1 disabled:opacity-50" data-testid="phase-save-btn">
            Mettre à jour
          </button>
        </div>
      </div>
    </div>
  );
}

const LANG = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
];

function ReportModal({ onClose, initialLang = "fr", orgPhase = null }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState(initialLang);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const [pdfLoading, setPdfLoading] = useState(false);

  const generate = async () => {
    setLoading(true); setReport(null);
    try {
      const r = await api.post("/executive/report", { language: lang, phase: orgPhase || undefined });
      setReport(r.data.report);
    } catch (e) {
      setReport(t("common.error") + " : " + (e?.response?.data?.detail || "génération impossible."));
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    if (!report) return;
    setPdfLoading(true);
    try {
      const r = await api.post(
        "/executive/report/pdf",
        { report, language: lang },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `noam-board-report-${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (e) {
      window.print();
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#378ADD]" />
            <div>
              <div className="noam-overline">{t("saas.executive.report_title")}</div>
              <div className="noam-display text-base font-semibold">{t("saas.executive.report_subtitle")}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 border-b border-white/5 flex items-center gap-3 flex-wrap">
          <Languages className="w-4 h-4 text-zinc-500" />
          {LANG.map((l) => (
            <button key={l.code} onClick={() => setLang(l.code)} className={`noam-pill text-xs ${lang === l.code ? "selected" : ""}`} data-testid={`report-lang-${l.code}`}>
              {l.label}
            </button>
          ))}
          <div className="flex-1" />
          <button onClick={generate} disabled={loading} className="btn-primary text-xs disabled:opacity-50" data-testid="report-generate-btn">
            {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("saas.executive.generating")}</> : <><RefreshCw className="w-3.5 h-3.5" /> {report ? t("saas.executive.regenerate") : t("saas.executive.generate")}</>}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!report && !loading && (
            <>
              <p className="text-zinc-400 text-sm">
                {t("saas.executive.report_intro")}
              </p>
              <div className="pt-4 border-t border-white/5">
                <DocxUploader
                  module="configurer"
                  title={t("saas.executive.strategic_docs")}
                  compact
                />
                <p className="text-[11px] text-zinc-500 mt-2">
                  {t("saas.executive.strategic_docs_help")}
                </p>
              </div>
            </>
          )}
          {loading && (
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              <span className="ml-2">{t("saas.executive.generating")}</span>
            </div>
          )}
          {report && (
            <AiText copyable={false} testId="report-text">{report}</AiText>
          )}
        </div>

        {report && (
          <div className="p-5 border-t border-white/5 flex items-center gap-2">
            <CopyButton text={report} label={t("common.copy")} />
            <PersonaAction action="export_pdf">
              <button onClick={exportPdf} disabled={pdfLoading} className="btn-ghost text-xs disabled:opacity-50" data-testid="report-pdf-btn">
                {pdfLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("saas.executive.exporting")}</> : t("saas.executive.export_pdf")}
              </button>
            </PersonaAction>
            <div className="flex-1" />
            <button onClick={onClose} className="btn-primary text-xs">{t("common.close")}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function PhaseBanner({ phase, linkBase }) {
  const { t } = useTranslation();
  const CONFIG = {
    "Stabilisation": {
      color: "#378ADD",
      bg: "rgba(55,138,221,0.12)",
      border: "rgba(55,138,221,0.40)",
      icon: Target,
      href: `${linkBase}/risques`,
    },
    "Déploiement": {
      color: "#1D9E75",
      bg: "rgba(29,158,117,0.12)",
      border: "rgba(29,158,117,0.40)",
      icon: Activity,
      href: `${linkBase}/risques`,
    },
    "Restructuration": {
      color: "#EF9F27",
      bg: "rgba(239,159,39,0.12)",
      border: "rgba(239,159,39,0.40)",
      icon: AlertTriangle,
      href: `${linkBase}/risques`,
    },
  };
  const cfg = CONFIG[phase] || CONFIG["Stabilisation"];
  const Icon = cfg.icon;
  return (
    <div
      className="rounded-xl px-5 py-4 flex items-center gap-4 border"
      style={{ background: cfg.bg, borderColor: cfg.border }}
      data-testid="exec-phase-banner"
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}
      >
        <Icon className="w-5 h-5" style={{ color: cfg.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: cfg.color }}>
          {t("executive.phase_banner.label")} · {t(`settings.values.phase.${phase}`, phase)}
        </div>
        <div className="noam-display text-base font-semibold text-white mt-0.5">
          {t(`executive.phase_banner.${phase}.title`)}
        </div>
        <div className="text-xs text-zinc-400 mt-1">
          {t(`executive.phase_banner.${phase}.help`)}
        </div>
      </div>
      <Link
        to={cfg.href}
        className="hidden sm:inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border transition-colors hover:bg-white/5 shrink-0"
        style={{ borderColor: cfg.border, color: cfg.color }}
        data-testid="exec-phase-banner-cta"
      >
        {t(`executive.phase_banner.${phase}.cta`)} <ArrowUpRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}


function fmtBytesShort(n) {
  if (n == null) return "—";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} Mo`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} Go`;
}

function DriveWidget({ linkBase }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/workspace/documents/aggregate/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const pct = data.total_bytes ? Math.min(100, Math.round((data.used_bytes / data.total_bytes) * 100)) : 0;
  const c = data.alert_level === "full" ? "#E24B4A" : data.alert_level === "warn" ? "#EF9F27" : "#1D9E75";
  return (
    <div className="noam-card p-5" data-testid="exec-drive">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="noam-overline">{t("exec.drive_overline", { defaultValue: "Noam Drive" })}</div>
          <div className="noam-display text-base font-semibold">{t("exec.drive_title", { defaultValue: "Documents & stockage" })}</div>
        </div>
        <Link to={`${linkBase}/documents`} className="text-xs text-[#378ADD] inline-flex items-center gap-1" data-testid="exec-drive-open">
          {t("exec.drive_open", { defaultValue: "Ouvrir Noam Drive" })} <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="rounded-md border border-white/5 p-3 bg-white/[0.02]" data-testid="exec-drive-count">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.drive_count", { defaultValue: "Documents" })}</div>
          <div className="noam-display text-2xl font-bold mt-1">{data.count || 0}</div>
        </div>
        <div className="rounded-md border border-white/5 p-3 bg-white/[0.02]" data-testid="exec-drive-quota">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.drive_used", { defaultValue: "Espace utilisé" })}</div>
          <div className="noam-display text-base font-bold mt-1" style={{ color: c }}>
            {fmtBytesShort(data.used_bytes)} / {data.total_bytes == null ? "∞" : fmtBytesShort(data.total_bytes)}
          </div>
          <div className="h-1.5 mt-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full" style={{ width: `${pct}%`, background: c }} />
          </div>
        </div>
        <div className="rounded-md border border-white/5 p-3 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("exec.drive_recent", { defaultValue: "Récents" })}</div>
          {(data.recent || []).slice(0, 3).map((d) => (
            <div key={d.id} className="text-[11px] text-zinc-300 truncate mt-0.5" title={d.name}>
              <FolderOpen className="w-2.5 h-2.5 inline mr-1 text-[#378ADD]" />
              {d.name}
            </div>
          ))}
          {(!data.recent || data.recent.length === 0) && (
            <div className="text-[11px] text-zinc-500 mt-1">—</div>
          )}
        </div>
      </div>
    </div>
  );
}
